import { getModalDraft, saveModalDraft } from '@/core/api/modalDraft'
import { useMemo } from 'react'
import { useApp } from '@/core/state/appStore'
import type { TabOverrides } from '@/core/components/modal/ModalHeader'
import {
  ShellFormularBox,
  type ShellFormularApi,
  type ShellFormularTexte,
} from '@/core/components/form/ShellFormularBox'
import { aufnahmePotenzialanalyseProbleme } from '../../api/aufnahmePotenzialanalyse'
import {
  getBewertungsbogen,
  saveBewertungsbogen,
  type Ausgefuellt,
  type BewertungsbogenGespeichert,
  type Bogen,
} from '../../api/bewertungsbogen'
import { AUFGABE, type AufgabeEntry } from '../../aufgabe'
import { vonAmVermerk } from '@/core/lib/dates'

export function ausgefuelltVermerk(ausgefuellt: Ausgefuellt): string {
  return vonAmVermerk('Ausgefüllt', ausgefuellt)
}

const TEXTE: Record<Bogen, ShellFormularTexte> = {
  jobcoach: {
    name: 'Bewertungsbogen Jobcoach',
    gespeichert: 'Bewertungsbogen Jobcoach gespeichert',
    ladenFehlgeschlagen: 'Der Bewertungsbogen Jobcoach konnte nicht geladen werden.',
    speichernFehlgeschlagen: 'Der Bogen wurde nicht gespeichert, bitte speichern Sie erneut.',
  },
  anleiter: {
    name: 'Bewertungsbogen Fachanleiter',
    gespeichert: 'Bewertungsbogen Fachanleiter gespeichert',
    ladenFehlgeschlagen: 'Der Bewertungsbogen Fachanleiter konnte nicht geladen werden.',
    speichernFehlgeschlagen: 'Der Bogen wurde nicht gespeichert, bitte speichern Sie erneut.',
  },
  sprachlehrkraft: {
    name: 'Bewertungsbogen Sprachlehrkraft',
    gespeichert: 'Bewertungsbogen Sprachlehrkraft gespeichert',
    ladenFehlgeschlagen: 'Der Bewertungsbogen Sprachlehrkraft konnte nicht geladen werden.',
    speichernFehlgeschlagen: 'Der Bogen wurde nicht gespeichert, bitte speichern Sie erneut.',
  },
}

const FUER: Record<Bogen, string> = {
  jobcoach: 'Für den Jobcoach',
  anleiter: 'Für den Fachanleiter',
  sprachlehrkraft: 'Für die Sprachlehrkraft',
}

function api(bogen: Bogen): ShellFormularApi<BewertungsbogenGespeichert> {
  return {
    laden: async (coachingId) => {
      const [{ ausgefuellt, ...stand }, entwurf] = await Promise.all([
        getBewertungsbogen(coachingId, bogen),
        getModalDraft(`/bewertungsbogen/${coachingId}/${bogen}/entwurf`),
      ])
      return { ...stand, werte: entwurf ?? stand.werte, vermerk: ausgefuellt && ausgefuelltVermerk(ausgefuellt) }
    },
    entwurfSpeichern: (coachingId, werte) => saveModalDraft(`/bewertungsbogen/${coachingId}/${bogen}/entwurf`, werte),
    speichern: (coachingId, werte) => saveBewertungsbogen(coachingId, bogen, werte),
    probleme: aufnahmePotenzialanalyseProbleme,
  }
}

const API: Record<Bogen, ShellFormularApi<BewertungsbogenGespeichert>> = {
  jobcoach: api('jobcoach'),
  anleiter: api('anleiter'),
  sprachlehrkraft: api('sprachlehrkraft'),
}

const AUFGABEN: readonly AufgabeEntry[] = Object.values(AUFGABE)

export function Bewertungsbogen({
  coachingId,
  bogen,
  tabs,
}: {
  coachingId: string | null
  bogen: Bogen
  tabs?: TabOverrides
}) {
  const { dispatch } = useApp()
  const boxApi = useMemo<ShellFormularApi<BewertungsbogenGespeichert>>(
    () => ({
      ...API[bogen],
      nachSpeichern: (_id, { aufgabe }) => {
        const landing = AUFGABEN.find((e) => e.dbName === aufgabe)?.landing
        dispatch(landing ? { type: 'CHANGE_PHASE', target: { ...landing } } : { type: 'CLOSE' })
      },
    }),
    [bogen, dispatch],
  )
  return (
    <ShellFormularBox
      coachingId={coachingId}
      api={boxApi}
      texte={TEXTE[bogen]}
      tabs={tabs}
      meldetNichtGespeichert
      speichernText="Bogen abschließen"
      fuer={FUER[bogen]}
      fragen
    />
  )
}
